import { Controller, useForm } from 'react-hook-form';
import * as z from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { StyledButton } from '@app/components/button/Button';
import { StyledButtonGroup } from '@app/components/button-group/ButtonGroup';
import { TextField } from '@app/components/form-fields/TextField';

type SaveFlowProps = {
    onCancel: () => void;
    onSave: (fileName: string) => void;
    initialValues: { name: string };
    saveBtnText: 'Export' | 'Save';
};

const DEFAULT_VALUES = {
    name: '',
};

const ExportFormSchema = z.object({
    name: z.string().trim().min(1, 'Name is required'),
});

type SaveFlowFormValues = typeof DEFAULT_VALUES;

const SaveFlowForm = ({ onCancel, onSave, initialValues, saveBtnText }: SaveFlowProps) => {
    const {
        handleSubmit,
        control,
        reset,
        formState: { isValid, isSubmitting },
    } = useForm({
        resolver: zodResolver(ExportFormSchema),
        defaultValues: initialValues || DEFAULT_VALUES,
    });

    const onSubmit = (data: SaveFlowFormValues) => {
        onSave(data.name);
    };

    const onCancelClick = () => {
        reset(DEFAULT_VALUES);
        onCancel();
    };

    return (
        <form onSubmit={handleSubmit(onSubmit)} className="save-flow-form">
            <div>
                <Controller
                    control={control}
                    name="name"
                    render={({ field, fieldState }) => (
                        <TextField
                            fieldState={fieldState}
                            {...field}
                            label="Flow name"
                            type="text"
                            placeholder="Enter flow name"
                            autoFocus
                            required
                        />
                    )}
                />
            </div>
            <StyledButtonGroup justify="space-between">
                <StyledButton htmlType="button" onClick={onCancelClick}>
                    Cancel
                </StyledButton>
                <StyledButton
                    htmlType="submit"
                    type="primary"
                    disabled={!isValid}
                    loading={isSubmitting}>
                    {saveBtnText}
                </StyledButton>
            </StyledButtonGroup>
        </form>
    );
};

export default SaveFlowForm;
